import React, { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Edit, Trash2, Save, X, MapPin, Calendar, Users, DollarSign, 
  Image as ImageIcon, Star, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedVariants, setExpandedVariants] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    destination: '',
    image_url: '',
    category: 'Heritage',
    rating: 5,
    featured: false,
    is_active: true,
    benefits: [],
    help_title: '',
    help_description: '',
    help_phone: '',
    weight: 0,
    broader_region: '',
    duration_variants: []
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const categories = ['Heritage', 'Nature', 'Spiritual', 'Adventure', 'All Tours'];

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setImageFile(null);
    setImagePreview('');
    setFormData({
      name: '',
      description: '',
      destination: '',
      image_url: '',
      category: 'Heritage',
      rating: 5,
      featured: false,
      is_active: true,
      benefits: ['Best price guarantee', 'Free cancellation up to 48 hours', '24/7 customer support'],
      help_title: 'Need Help?',
      help_description: 'Our travel experts are here to assist you',
      help_phone: '+918001234567',
      duration_variants: [{
        duration_days: 2,
        duration_text: '2 Days 1 Night',
        price: 0,
        max_people: 10,
        inclusions: [],
        exclusions: [],
        itinerary: [
          { day: 1, title: 'Day 1', activities: '' }
        ]
      }]
    });
  };

  const handleEdit = (pkg) => {
    setEditingId(pkg._id || pkg.id);
    setImageFile(null);
    setImagePreview(pkg.image_url || '');
    setFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      destination: pkg.destination || '',
      image_url: pkg.image_url || '',
      category: pkg.category || 'Heritage',
      rating: pkg.rating || 5,
      featured: pkg.featured || false,
      is_active: pkg.is_active !== undefined ? pkg.is_active : true,
      benefits: pkg.benefits || ['Best price guarantee', 'Free cancellation up to 48 hours', '24/7 customer support'],
      help_title: pkg.help_title || 'Need Help?',
      help_description: pkg.help_description || 'Our travel experts are here to assist you',
      help_phone: pkg.help_phone || '+918001234567',
      duration_variants: pkg.duration_variants || []
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setImageFile(null);
    setImagePreview('');
    setFormData({
      name: '',
      description: '',
      destination: '',
      image_url: '',
      category: 'Heritage',
      rating: 5,
      featured: false,
      is_active: true,
      benefits: [],
      help_title: '',
      help_description: '',
      help_phone: '',
      duration_variants: []
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (!formData.name || !formData.destination) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (formData.duration_variants.length === 0) {
        toast.error('Please add at least one duration variant');
        return;
      }

      // Upload image if new image selected
      let imageUrl = formData.image_url;
      if (imageFile) {
        try {
          imageUrl = await uploadImage();
        } catch (error) {
          toast.error('Failed to upload image');
          return;
        }
      }

      const payload = {
        ...formData,
        image_url: imageUrl,
        rating: parseFloat(formData.rating)
      };

      if (isCreating) {
        await axiosInstance.post(`${API_URL}/api/admin/packages`, payload);
        toast.success('Package created successfully');
      } else {
        await axiosInstance.put(`${API_URL}/api/admin/packages/${editingId}`, payload);
        toast.success('Package updated successfully');
      }

      fetchPackages();
      handleCancel();
    } catch (error) {
      console.error('Failed to save package:', error);
      toast.error(error.response?.data?.detail || 'Failed to save package');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await axiosInstance.delete(`${API_URL}/api/admin/packages/${id}`);
      toast.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Failed to delete package:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete package');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const uploadImage = async () => {
    if (!imageFile) {
      return formData.image_url; // Return existing URL if no new image
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', imageFile);

      const response = await axios.post(`${API_URL}/api/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data.url;
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Duration Variant Management
  const addVariant = () => {
    const newDay = formData.duration_variants.length + 1;
    setFormData(prev => ({
      ...prev,
      duration_variants: [
        ...prev.duration_variants,
        {
          duration_days: newDay,
          duration_text: `${newDay} Days ${newDay - 1} Night${newDay > 2 ? 's' : ''}`,
          price: 0,
          max_people: 10,
          inclusions: [],
          exclusions: [],
          itinerary: Array.from({ length: newDay }, (_, i) => ({
            day: i + 1,
            title: `Day ${i + 1}`,
            activities: ''
          }))
        }
      ]
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      duration_variants: prev.duration_variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => {
      const newVariants = [...prev.duration_variants];
      newVariants[index] = { ...newVariants[index], [field]: value };
      return { ...prev, duration_variants: newVariants };
    });
  };

  const updateVariantItinerary = (variantIndex, dayIndex, value) => {
    setFormData(prev => {
      const newVariants = [...prev.duration_variants];
      const newItinerary = [...newVariants[variantIndex].itinerary];
      newItinerary[dayIndex] = { ...newItinerary[dayIndex], activities: value };
      newVariants[variantIndex] = { ...newVariants[variantIndex], itinerary: newItinerary };
      return { ...prev, duration_variants: newVariants };
    });
  };

  const addItineraryDay = (variantIndex) => {
    setFormData(prev => {
      const newVariants = [...prev.duration_variants];
      const currentItinerary = newVariants[variantIndex].itinerary || [];
      const newDay = currentItinerary.length + 1;
      newVariants[variantIndex] = {
        ...newVariants[variantIndex],
        duration_days: newDay,
        duration_text: `${newDay} Days ${newDay - 1} Night${newDay > 2 ? 's' : ''}`,
        itinerary: [
          ...currentItinerary,
          { day: newDay, title: `Day ${newDay}`, activities: '' }
        ]
      };
      return { ...prev, duration_variants: newVariants };
    });
  };

  const removeItineraryDay = (variantIndex, dayIndex) => {
    setFormData(prev => {
      const newVariants = [...prev.duration_variants];
      const newItinerary = newVariants[variantIndex].itinerary.filter((_, i) => i !== dayIndex);
      newVariants[variantIndex] = {
        ...newVariants[variantIndex],
        duration_days: newItinerary.length,
        duration_text: `${newItinerary.length} Days ${newItinerary.length - 1} Night${newItinerary.length > 2 ? 's' : ''}`,
        itinerary: newItinerary.map((day, i) => ({ ...day, day: i + 1, title: `Day ${i + 1}` }))
      };
      return { ...prev, duration_variants: newVariants };
    });
  };

  const updateInclusionsExclusions = (variantIndex, type, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData(prev => {
      const newVariants = [...prev.duration_variants];
      newVariants[variantIndex] = { ...newVariants[variantIndex], [type]: items };
      return { ...prev, duration_variants: newVariants };
    });
  };

  const toggleVariantExpand = (index) => {
    setExpandedVariants(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getMinPrice = (pkg) => {
    if (!pkg.duration_variants || pkg.duration_variants.length === 0) {
      return pkg.price || 0;
    }
    return Math.min(...pkg.duration_variants.map(v => v.price || 0));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Travel Packages</h1>
            <p className="text-gray-600 mt-1">Manage NammaYatra tour packages with configurable durations</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-green-600 hover:bg-green-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Package</span>
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingId) && (
          <Card className="border-2 border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center justify-between">
                <span>{isCreating ? 'Create New Package' : 'Edit Package'}</span>
                <Button variant="ghost" size="icon" onClick={handleCancel}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Basic Package Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Package Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Mysore Palace Heritage Tour"
                    />
                  </div>

                  <div>
                    <Label htmlFor="destination">Destination *</Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      placeholder="e.g., Mysore, Karnataka"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="rating">Rating (1-5)</Label>
                    <Input
                      id="rating"
                      name="rating"
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={formData.rating}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div>
                  <Label>Package Image *</Label>
                  <div className="mt-2">
                    {imagePreview ? (
                      <div className="relative w-full h-64 border-2 border-gray-300 rounded-lg overflow-hidden">
                        <img
                          src={imagePreview}
                          alt="Package preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors bg-gray-50">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="w-12 h-12 text-gray-400 mb-3" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG or WEBP (MAX. 5MB)</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Brief description of the package"
                  />
                </div>

                <div>
                  <Label>Package Benefits (one per line)</Label>
                  <Textarea
                    value={formData.benefits?.join('\n') || ''}
                    onChange={(e) => {
                      const benefits = e.target.value.split('\n').filter(b => b.trim());
                      setFormData(prev => ({ ...prev, benefits }));
                    }}
                    rows={4}
                    placeholder="e.g.,&#10;Best price guarantee&#10;Free cancellation up to 48 hours&#10;24/7 customer support&#10;Instant confirmation"
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 These benefits will be displayed on the package detail page</p>
                </div>

                {/* Help Section Configuration */}
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-semibold text-gray-900">Help Section (Customer Facing)</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="help_title">Help Title</Label>
                      <Input
                        id="help_title"
                        name="help_title"
                        value={formData.help_title}
                        onChange={handleInputChange}
                        placeholder="e.g., Need Help?"
                      />
                    </div>

                    <div>
                      <Label htmlFor="help_phone">Contact Phone Number</Label>
                      <Input
                        id="help_phone"
                        name="help_phone"
                        value={formData.help_phone}
                        onChange={handleInputChange}
                        placeholder="e.g., +918001234567"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="help_description">Help Description</Label>
                    <Input
                      id="help_description"
                      name="help_description"
                      value={formData.help_description}
                      onChange={handleInputChange}
                      placeholder="e.g., Our travel experts are here to assist you"
                    />
                  </div>
                  
                  <p className="text-xs text-gray-500">💡 This will be displayed in the "Need Help?" card on the package detail page</p>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Featured Package</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Duration Variants Section */}
              <div className="space-y-4 border-t pt-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Duration Variants</h3>
                  <Button
                    type="button"
                    size="sm"
                    onClick={addVariant}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Variant
                  </Button>
                </div>

                {formData.duration_variants.map((variant, variantIndex) => (
                  <Card key={variantIndex} className="border-2 border-gray-200">
                    <CardHeader className="bg-gray-50 cursor-pointer" onClick={() => toggleVariantExpand(variantIndex)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Calendar className="w-5 h-5 text-green-600" />
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {variant.duration_text || `Variant ${variantIndex + 1}`}
                            </h4>
                            <p className="text-sm text-gray-600">
                              ₹{variant.price?.toLocaleString()} • {variant.itinerary?.length || 0} days
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {expandedVariants[variantIndex] ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeVariant(variantIndex);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>

                    {expandedVariants[variantIndex] && (
                      <CardContent className="p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label>Duration Text</Label>
                            <Input
                              value={variant.duration_text}
                              onChange={(e) => updateVariant(variantIndex, 'duration_text', e.target.value)}
                              placeholder="e.g., 3 Days 2 Nights"
                            />
                          </div>
                          <div>
                            <Label>Price (₹) *</Label>
                            <Input
                              type="number"
                              value={variant.price}
                              onChange={(e) => updateVariant(variantIndex, 'price', parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 15999"
                            />
                          </div>
                          <div>
                            <Label>Max People</Label>
                            <Input
                              type="number"
                              value={variant.max_people}
                              onChange={(e) => updateVariant(variantIndex, 'max_people', parseInt(e.target.value) || 10)}
                              placeholder="e.g., 10"
                            />
                          </div>
                        </div>

                        {/* Day-by-Day Itinerary */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-base font-semibold">Day-by-Day Itinerary</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => addItineraryDay(variantIndex)}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Day
                            </Button>
                          </div>
                          {variant.itinerary?.map((day, dayIndex) => (
                            <div key={dayIndex} className="flex items-start space-x-2 bg-gray-50 p-3 rounded-lg">
                              <div className="flex-shrink-0 w-20 pt-2">
                                <Badge className="bg-green-600">Day {day.day}</Badge>
                              </div>
                              <Textarea
                                value={day.activities}
                                onChange={(e) => updateVariantItinerary(variantIndex, dayIndex, e.target.value)}
                                rows={2}
                                placeholder={`Activities for Day ${day.day}`}
                                className="flex-1"
                              />
                              {variant.itinerary.length > 1 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeItineraryDay(variantIndex, dayIndex)}
                                  className="flex-shrink-0 text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Inclusions */}
                        <div>
                          <Label>Inclusions (one per line)</Label>
                          <Textarea
                            value={variant.inclusions?.join('\n') || ''}
                            onChange={(e) => updateInclusionsExclusions(variantIndex, 'inclusions', e.target.value)}
                            rows={3}
                            placeholder="e.g.,&#10;Accommodation&#10;Meals&#10;Transportation"
                          />
                        </div>

                        {/* Exclusions */}
                        <div>
                          <Label>Exclusions (one per line)</Label>
                          <Textarea
                            value={variant.exclusions?.join('\n') || ''}
                            onChange={(e) => updateInclusionsExclusions(variantIndex, 'exclusions', e.target.value)}
                            rows={3}
                            placeholder="e.g.,&#10;Airfare&#10;Travel insurance&#10;Personal expenses"
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}

                {formData.duration_variants.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-3">No duration variants added yet</p>
                    <Button
                      type="button"
                      onClick={addVariant}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Variant
                    </Button>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading || uploadingImage}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {uploadingImage ? 'Uploading image...' : (loading ? 'Saving...' : (isCreating ? 'Create Package' : 'Save Changes'))}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Packages List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card key={pkg._id || pkg.id} className="border hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                {/* Package Image */}
                <div className="relative h-48 bg-gradient-to-br from-green-400 to-green-600 rounded-t-lg overflow-hidden">
                  {pkg.image_url ? (
                    <img
                      src={pkg.image_url}
                      alt={pkg.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <ImageIcon className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}
                  {pkg.featured && (
                    <Badge className="absolute top-3 right-3 bg-yellow-400 text-yellow-900">
                      <Star className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  )}
                  <Badge className="absolute top-3 left-3 bg-white text-gray-900">
                    {pkg.category}
                  </Badge>
                </div>

                {/* Package Details */}
                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{pkg.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{pkg.description}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-green-600" />
                      <span>{pkg.destination}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2 text-green-600" />
                      <span>{pkg.duration_variants?.length || 0} duration options</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-lg font-bold text-green-700">
                        <span className="text-xs text-gray-500 mr-1">Starting from</span>
                        <span>₹{getMinPrice(pkg)?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center text-sm text-yellow-600">
                        <Star className="w-4 h-4 mr-1 fill-current" />
                        <span>{pkg.rating || 5}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 pt-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(pkg)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(pkg._id || pkg.id)}
                      className="flex-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && !loading && (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <MapPin className="w-16 h-16 text-gray-300" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No packages yet</h3>
                <p className="text-gray-600 mb-4">Create your first travel package to get started</p>
                <Button
                  onClick={handleCreate}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Package
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
